import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { UserMinus, UserPlus, Star } from 'lucide-react'
import { previewUrl as buildPreviewUrl } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'

interface FaceHoverPreviewProps {
  cropUrl: string
  faceId: string
  triggerRect: DOMRect
  onMouseEnter: () => void
  onMouseLeave: () => void
  onRemove?: (faceId: string) => void
  onAssign?: (faceId: string) => void
  onMakePrimary?: (faceId: string) => void
}

const PREVIEW_WIDTH = 200
const PREVIEW_HEIGHT = 240
const GAP = 8

export default function FaceHoverPreview({
  cropUrl,
  faceId,
  triggerRect,
  onMouseEnter,
  onMouseLeave,
  onRemove,
  onAssign,
  onMakePrimary,
}: FaceHoverPreviewProps) {
  useSignedUrls()
  const ref = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const [visible, setVisible] = useState(false)
  const [sourceHovered, setSourceHovered] = useState(false)

  // Derive source image preview URL from face_id
  const parts = faceId.split('_face_')
  const masterId = parts.length === 2 ? parts[0] : null
  const faceIndex = parts.length === 2 ? parts[1] : null
  const sourcePreviewUrl = masterId ? buildPreviewUrl(masterId) : null

  useEffect(() => {
    const vw = window.innerWidth
    const vh = window.innerHeight

    // Try right side first
    let left: number
    if (triggerRect.right + GAP + PREVIEW_WIDTH <= vw) {
      left = triggerRect.right + GAP
    } else {
      left = triggerRect.left - GAP - PREVIEW_WIDTH
    }

    // Center vertically on trigger, clamp to viewport
    let top = triggerRect.top + triggerRect.height / 2 - PREVIEW_HEIGHT / 2
    top = Math.max(8, Math.min(vh - PREVIEW_HEIGHT - 8, top))

    setPosition({ top, left })
    // Fade in after positioning
    requestAnimationFrame(() => setVisible(true))
  }, [triggerRect])

  return createPortal(
    <div
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`fixed z-[70] bg-card border border-border rounded-xl shadow-2xl p-3 pointer-events-auto transition-opacity duration-150 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ top: position.top, left: position.left, width: PREVIEW_WIDTH }}
    >
      {/* Large face crop */}
      <div className="w-full aspect-square rounded-lg overflow-hidden bg-secondary mb-2">
        <img
          src={cropUrl}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      {/* Source image thumbnail + face label */}
      <div className="flex items-center gap-2">
        {sourcePreviewUrl && (
          <div
            className={`rounded overflow-hidden bg-secondary flex-shrink-0 transition-all duration-200 cursor-pointer ${
              sourceHovered ? 'w-[150px] h-[112px]' : 'w-12 h-9'
            }`}
            onMouseEnter={() => setSourceHovered(true)}
          >
            <img
              src={sourcePreviewUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}
        {!sourceHovered && (
          <span className="text-xs text-muted-foreground">
            Face {faceIndex != null ? parseInt(faceIndex) + 1 : '?'}
          </span>
        )}
      </div>
      {/* Action buttons */}
      {(onRemove || onAssign || onMakePrimary) && (
        <div className="flex gap-1.5 mt-2 pt-2 border-t border-border flex-wrap">
          {onRemove && (
            <button
              onClick={() => onRemove(faceId)}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-red-600/10 hover:bg-red-600/20 text-red-400 transition-colors"
            >
              <UserMinus size={11} />
              Remove
            </button>
          )}
          {onAssign && (
            <button
              onClick={() => onAssign(faceId)}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-secondary hover:bg-secondary/80 text-foreground transition-colors"
            >
              <UserPlus size={11} />
              Assign
            </button>
          )}
          {onMakePrimary && (
            <button
              onClick={() => onMakePrimary(faceId)}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-amber-600/10 hover:bg-amber-600/20 text-amber-400 transition-colors"
            >
              <Star size={11} />
              Make Primary
            </button>
          )}
        </div>
      )}
    </div>,
    document.body
  )
}
